import { CheckIcon, ChevronDownIcon, IconSizeEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TIME_KEEPING_REVIEW_STATES } from '@/constants';
import { TimeKeepingReviewStateEnum } from '@/features/clips/types';

interface TimeKeepingStatusChipSelectProps {
  value: TimeKeepingReviewStateEnum;
  onChange: (value: TimeKeepingReviewStateEnum) => void;
}

export function TimeKeepingStatusChipSelect({ value, onChange }: TimeKeepingStatusChipSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedStatus = TIME_KEEPING_REVIEW_STATES.find(s => s.value === value)!;
  const SelectedIcon = selectedStatus.icon;

  const handleOptionClick = (next: TimeKeepingReviewStateEnum) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={clsx('w-[120px] appearance-none', selectedStatus.class)}
      >
        <span className="flex flex-1 items-center gap-1 text-xs font-semibold">
          <SelectedIcon size={IconSizeEnum.XS} />
          {t(selectedStatus.label)}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-[150px] rounded-md border border-gray-200 bg-white shadow-lg">
          {TIME_KEEPING_REVIEW_STATES.map(s => {
            const Icon = s.icon;
            return (
              <div
                key={s.value}
                onClick={() => handleOptionClick(s.value)}
                className="flex w-full cursor-pointer items-center gap-1 px-2 py-1 hover:bg-gray-100"
              >
                <span className={clsx('flex-1 text-xs font-semibold', s.class)}>
                  <Icon className="h-4 w-4" size={IconSizeEnum.XS} />
                  {t(s.label)}
                </span>
                {value === s.value && <CheckIcon size={IconSizeEnum.XS} />}
              </div>
            );
          })}
        </div>
      )}

      <ChevronDownIcon
        onClick={() => setOpen(prev => !prev)}
        size={IconSizeEnum.XS}
        className={clsx(
          'absolute right-2 top-1/2 -translate-y-1/2 transform cursor-pointer',
          open && '-rotate-180'
        )}
      />
    </div>
  );
}
